"use client";

import Link from "next/link";
import { APP_DEPLOY_URL as DEPLOY_URL } from "@/lib/site-config";

interface DeployButtonProps {
  /** "sm" for header/nav, "lg" for hero/CTA band. Default: "lg". */
  size?: "sm" | "lg";
  /** Analytics location tag. */
  location?: string;
  className?: string;
  /** Override CTA label. Default: "Start Building, Free". */
  label?: string;
}

/**
 * Primary CTA — Claude v2 button style.
 * Solid accent fill, 1px accent border, animated 2px underline on hover.
 * No gradient, no glow shadow, no scale-press — restrained / enterprise.
 */
export default function DeployButton({
  size = "lg",
  location,
  className = "",
  label = "Start Building, Free",
}: DeployButtonProps) {
  const isSmall = size === "sm";

  return (
    <Link
      href={DEPLOY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-cta="start-building-free"
      data-cta-location={location}
      className={`
        btn-v2-primary group relative
        inline-flex items-center justify-center gap-1.5
        overflow-hidden whitespace-nowrap
        font-medium text-white
        transition-[transform,filter,background-color] duration-[180ms] ease-[cubic-bezier(0.2,0,0,1)]
        hover:-translate-y-px hover:brightness-[0.92]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas
        ${isSmall ? "px-[14px] py-[7px] text-[13px] min-h-[40px]" : "px-[24px] py-[14px] text-[15px] min-h-[52px] sm:min-h-[48px]"}
        ${className}
      `}
      style={{
        background: "var(--accent)",
        border: "1px solid var(--accent)",
        borderRadius: "var(--radius-md)",
        lineHeight: 1,
        color: "#fff",
      }}
    >
      {label}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 bg-white/50 transition-transform duration-[240ms] ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-x-100"
      />
    </Link>
  );
}
